import { Stack } from 'component-library';

import { IndicatorName } from '@/api/rest/resources/types/fact';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { CanopyHeightHistoricalChart } from '@/components/Charts';
import { ItemizedGraphTypeRenderer } from '@/components/Charts/components/ItemizedGraphType';
import { NUM_ITEMS_TYPE } from '@/components/Charts/hooks/usePlotsForItemizedGraph';
import { GraphFact } from '@/components/Charts/types';
import { GraphTileProps } from '@/components/IndicatorCard/GraphTile';
import { useNCCardContext } from '@/components/NCCard/NCCard';
import { useGetCustomBenchmarks } from '@/hooks/useCustomBenchmarks';
import { useScaledCustomBenchmark } from '@/hooks/useScaledCustomBenchmark';
import { useOrganizationIndicatorGraphFact } from '@/pages/shared/hooks/useOrganizationFact';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { NoData } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/components/NoData';

export const CanopyHeightGraphTile = (props?: (GraphTileProps | null) & { selectedBenchmark?: string | null }) => {
  const plotGroupId = props?.plotGroupId ?? null;
  const selectedBenchmark = props?.selectedBenchmark ?? null;
  const graphType = props?.graphType ?? undefined;

  const canopyHeightHistoricFact = IndicatorName.canopy_height_graph;
  const { [canopyHeightHistoricFact]: canopyHeightHistoric } = useOrganizationIndicatorGraphFact<GraphFact[]>(
    canopyHeightHistoricFact,
    null,
    undefined,
    plotGroupId,
  );
  const chartData = canopyHeightHistoric?.value;
  const noChartData = !chartData?.length;

  const organizationId = useOrganizationId();
  const indicator = GraphTileType.canopy_height;
  const { data: customBenchmarksResp } = useGetCustomBenchmarks(organizationId, indicator);
  const activeCustomBenchmark = customBenchmarksResp?.results?.find((b) => b.id === selectedBenchmark);
  const { analysisType } = useNCCardContext();
  const isTotal = analysisType === 'total';
  const displayCustomBenchmark = useScaledCustomBenchmark(activeCustomBenchmark, isTotal);

  if (noChartData) {
    return <NoData />;
  }

  return (
    <Stack center data-testid='canopy-height-graph-tile'>
      <Stack spacing={2.5} className='w-full items-center justify-center px-1 py-6 lg:items-start lg:p-8 lg:pl-1'>
        <ItemizedGraphTypeRenderer
          graphType={graphType}
          indicator={GraphTileType.canopy_height}
          plotRenderType={props?.plotRenderType ?? NUM_ITEMS_TYPE.TOP_PLOTS}
          plotGroupId={plotGroupId ?? undefined}
          customBenchmark={displayCustomBenchmark}
          defaultGraphType={
            <CanopyHeightHistoricalChart
              data={chartData}
              showTooltip={true}
              customBenchmark={displayCustomBenchmark}
              data-testid='canopy-height-historic-chart'
            />
          }
        />
      </Stack>
    </Stack>
  );
};
